import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { categoryLinks } from "@/mocks/categories";
import { allArticles, type ArticleData } from "@/mocks/articles";

const siteUrl = import.meta.env.VITE_SITE_URL || "https://vigorbuy.cfd";
const defaultImage = "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/a9b8fc4c2048f16265644a3fc912425a.jpeg";

function ArticleJsonLd({ article, url }: { article: ArticleData; url: string }) {
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          "@id": `${url}/#article`,
          headline: article.title,
          description: article.excerpt,
          image: article.imageUrl || defaultImage,
          url,
          datePublished: "2026-06-28T00:00:00+00:00",
          dateModified: "2026-06-28T00:00:00+00:00",
          author: {
            "@type": "Organization",
            name: "VigorBuy Resource Hub",
            url: siteUrl,
          },
          publisher: {
            "@type": "Organization",
            name: "VigorBuy Resource Hub",
            logo: {
              "@type": "ImageObject",
              url: defaultImage,
            },
          },
          mainEntityOfPage: {
            "@type": "WebPage",
            "@id": url,
          },
          about: {
            "@type": "Thing",
            name: "VigorBuy Spreadsheet",
          },
          wordCount:
            article.content
              .filter((s) => s.type === "p" || s.type === "h2" || s.type === "h3")
              .reduce((acc, s) => acc + (s.text ? s.text.split(" ").length : 0), 0),
        }),
      }}
    />
  );
}

function ArticleContent({ article }: { article: ArticleData }) {
  return (
    <div className="prose-custom text-foreground-800 leading-relaxed">
      {article.content.map((section, i) => {
        if (section.type === "h2") {
          return (
            <h2 key={i} className="text-2xl md:text-3xl font-heading text-foreground-900 mt-12 mb-4">
              {section.text}
            </h2>
          );
        }
        if (section.type === "h3") {
          return (
            <h3 key={i} className="text-lg font-semibold text-foreground-900 mt-8 mb-2">
              {section.text}
            </h3>
          );
        }
        if (section.type === "list" && section.items) {
          return (
            <ul key={i} className="list-disc pl-5 mb-4 space-y-1">
              {section.items.map((item, j) => (
                <li key={j} className="text-base text-foreground-700">{item}</li>
              ))}
            </ul>
          );
        }
        if (section.type === "cta") {
          return (
            <div key={i} className="bg-primary-50 border border-primary-200 rounded-xl p-6 md:p-8 mt-10 mb-10">
              <h3 className="text-xl font-heading text-foreground-900 mb-3">
                Ready to Explore Further?
              </h3>
              <p className="text-sm text-foreground-600 mb-5">{section.text}</p>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-white text-sm font-medium rounded-full hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  Back to Home
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-left-line text-sm"></i>
                  </div>
                </Link>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 px-6 py-2.5 border-2 border-primary-500 text-primary-600 text-sm font-medium rounded-full hover:bg-primary-500 hover:text-white transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  Visit Main Store
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-up-line text-sm"></i>
                  </div>
                </a>
              </div>
            </div>
          );
        }
        if (section.type === "p") {
          return (
            <p key={i} className="text-base text-foreground-700 mb-4">
              {section.text}
            </p>
          );
        }
        return null;
      })}

      {/* Internal link to home at the end */}
      <div className="mt-12 pt-8 border-t border-background-200/70">
        <p className="text-sm text-foreground-500 mb-3">
          <strong className="text-foreground-700">Explore more:</strong> Return to the{" "}
          <Link to="/" className="text-primary-600 hover:text-primary-700 underline">
            VigorBuy Resource Hub homepage
          </Link>{" "}
          to browse all 14 product categories, read additional guides, and discover the latest spreadsheet updates.
        </p>
      </div>
    </div>
  );
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const effectiveSlug = slug || "vigorbuy-spreadsheet-guide";
  const article = allArticles.find((a) => a.slug === effectiveSlug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [effectiveSlug]);

  if (!article) {
    return <Navigate to="/" replace />;
  }

  const articleUrl = `${siteUrl}/article/${effectiveSlug}`;

  return (
    <main>
      {/* ====== Schema.org JSON-LD: BlogPosting ====== */}
      <ArticleJsonLd article={article} url={articleUrl} />

      <Navbar />

      {/* Article Header */}
      <section className="w-full bg-background-100 pt-24 pb-12 md:pt-32 md:pb-16">
        <div className="w-full px-6 md:px-10 max-w-4xl mx-auto">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6">
            <Link to="/" className="hover:text-primary-600 transition-colors duration-200">
              Home
            </Link>
            <span>/</span>
            <span className="text-foreground-700 font-medium">{article.title}</span>
          </nav>

          <span className="inline-block px-3 py-1 text-xs font-medium text-primary-700 bg-primary-100 rounded-full mb-4">
            {article.tag}
          </span>
          <h1 className="text-3xl md:text-5xl font-heading text-foreground-900 mb-4 leading-tight">
            {article.title}
          </h1>
          <p className="text-sm md:text-base text-foreground-600 leading-relaxed max-w-2xl">
            {article.excerpt}
          </p>
          <div className="flex items-center gap-3 mt-6 text-xs text-foreground-400">
            <span>Updated: June 2026</span>
            <span>&bull;</span>
            <span>8 min read</span>
          </div>
        </div>
      </section>

      {/* Article Image */}
      <section className="w-full bg-background-50">
        <div className="w-full px-6 md:px-10 max-w-4xl mx-auto">
          <div className="w-full h-[280px] md:h-[420px] rounded-2xl overflow-hidden">
            <img
              src={article.imageUrl}
              alt={article.title}
              className="w-full h-full object-cover object-center"
              loading="eager"
            />
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="w-full bg-background-50 py-10 md:py-14">
        <div className="w-full px-6 md:px-10 max-w-3xl mx-auto">
          <ArticleContent article={article} />
        </div>
      </article>

      {/* Related Categories */}
      <section className="w-full bg-background-100 py-12 md:py-16">
        <div className="w-full px-6 md:px-10 max-w-4xl mx-auto">
          <h2 className="text-xl md:text-2xl font-heading text-foreground-900 mb-6 text-center">
            Explore Product Categories
          </h2>
          <div className="flex flex-wrap justify-center gap-2">
            {categoryLinks.map((cat) => (
              <a
                key={cat.name}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-background-50 border border-background-200/70 rounded-full text-sm text-foreground-600 hover:bg-primary-50 hover:text-primary-700 hover:border-primary-200 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`${cat.icon} text-xs`}></i>
                </div>
                {cat.name}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Related Articles */}
      <section className="w-full bg-background-50 py-12 md:py-16">
        <div className="w-full px-6 md:px-10 max-w-4xl mx-auto">
          <h2 className="text-xl md:text-2xl font-heading text-foreground-900 mb-6 text-center">
            More Guides &amp; Resources
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {allArticles
              .filter((a) => a.slug !== article.slug)
              .slice(0, 6)
              .map((related) => (
                <Link
                  key={related.slug}
                  to={`/article/${related.slug}`}
                  className="group block bg-background-100 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-200 cursor-pointer"
                >
                  <div className="w-full h-36 overflow-hidden">
                    <img
                      src={related.imageUrl}
                      alt={related.title}
                      className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-4">
                    <span className="inline-block px-2 py-0.5 text-xs font-medium text-primary-700 bg-primary-100 rounded-full mb-2">
                      {related.tag}
                    </span>
                    <h3 className="text-sm font-semibold text-foreground-900 leading-snug group-hover:text-primary-600 transition-colors duration-200 line-clamp-2">
                      {related.title}
                    </h3>
                  </div>
                </Link>
              ))}
          </div>
        </div>
      </section>

      {/* Bottom Navigation */}
      <section className="w-full bg-background-50 pb-10">
        <div className="w-full px-6 md:px-10 max-w-4xl mx-auto text-center">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-foreground-500 hover:text-primary-600 transition-colors duration-200"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-left-line text-sm"></i>
            </div>
            Return to VigorBuy Resource Hub Homepage
          </Link>
        </div>
      </section>

      <Footer />
    </main>
  );
}